import { Link } from "@/components/Link";
import {
  NameCol,
  NumCol,
  SmallerNameCol,
  SmallerNumCol,
} from "@/components/ProjectsTable/styled";
import { GetDropDownFilterProps } from "@/components/TableFilters/components/GetDropDownFilterProps";
import { GetSearchFilterProps } from "@/components/TableFilters/components/GetSearchFilterProps";
import {
  DATE_FORMATS,
  EntityStatus,
  FormType,
  dashboardFieldNames,
} from "@/enums";
import {
  generateDropDownOptions,
  getOrderBy,
  getSortOrder,
  routes,
} from "@/helpers";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetProductCategoryFormsQuery } from "@/services";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const configureColumnSorting = (sortOrder, key, column) => {
  if (!sortOrder) return column;

  return {
    ...column,
    key: key,
    sortOrder: sortOrder[key],
    className: "ant-table-column-has-filter",
    sorter: true,
    colConfigurator: {
      required: true,
      noFormProperty: true,
    },
  };
};

function setQuery(key, value, queryParams, setQueryParams) {
  const updatedQuery = { ...queryParams, page: 1 };
  updatedQuery[key] = value;
  setQueryParams(updatedQuery);
}

export function useColumnFactory(queryParams, setQueryParams) {
  const intl = useIntl();
  const [sortOrder, setSortOrder] = useState({});
  const [projectForm, setProjectForm] = useState(null);
  const { data: forms } = useGetProductCategoryFormsQuery();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  useEffect(() => {
    setProjectForm(forms?.intake);
  }, [forms?.intake]);

  useEffect(() => {
    setSortOrder(getSortOrder(queryParams?.orderBy));
  }, [queryParams]);

  const handleChange = (pagination, filters, sorter) => {
    handleSort(sorter);
  };

  const handleSort = (sorter) => {
    setQueryParams(getOrderBy(sorter));
  };

  function createMilestoneViewLinkColumn(title, linkLabel) {
    return {
      title,
      render: ({ id, projectId, milestoneId, scheduleId }) => {
        const toLink = routes.projects.getMilestone(
          projectId,
          milestoneId,
          scheduleId
        );
        return (
          <Button data-test-id={`project-table-intake-link-${id}`} type="link">
            <Link to={toLink}>{linkLabel}</Link>
          </Button>
        );
      },
    };
  }

  function createIntakeViewLinkColumn(title, linkLabel) {
    return {
      title,
      render: ({ id }) => {
        const toLink = routes.projects.intakeView(id);
        return (
          <Button data-test-id={`project-table-intake-link-${id}`} type="link">
            <Link to={toLink}>{linkLabel}</Link>
          </Button>
        );
      },
    };
  }

  function createCustomSortableSmallerNumColumn(
    title,
    fieldName,
    sortFieldName
  ) {
    let column = {
      title,
      render: (data) => <SmallerNumCol>{data[fieldName]}</SmallerNumCol>,
    };
    column = configureColumnSorting(sortOrder, sortFieldName, column);
    return column;
  }

  function createFilterableSortableDropdownColumn(
    title,
    fieldName,
    labelFieldName,
    dropdownOptions
  ) {
    let column = {
      title,
      render: (data) =>
        data[labelFieldName] ||
        getPropertyValueDisplay(FormType.Intake, fieldName, data[fieldName]),
      ...GetDropDownFilterProps({
        setQueryParamFunction: (key, value) => {
          setQuery(key, value, queryParams, setQueryParams);
        },
        queryParamKey: _.camelCase(fieldName),
        queryParamValue: queryParams[_.camelCase(fieldName)],
        options:
          dropdownOptions && dropdownOptions.length > 0
            ? dropdownOptions
            : generateDropDownOptions(fieldName, projectForm, EntityStatus),
      }),
    };

    column = configureColumnSorting(sortOrder, labelFieldName, column);
    return column;
  }

  function createFilterableSortableNumberColumn(title, fieldName) {
    let column = {
      title,
      render: (data) => <NumCol>{data[fieldName]}</NumCol>,
      ...GetSearchFilterProps({
        setQueryParamFunction: (key, value) => {
          setQuery(key, value, queryParams, setQueryParams);
        },
        queryParamKey: _.camelCase(fieldName),
        queryParamValue: queryParams[_.camelCase(fieldName)],
      }),
    };
    column = configureColumnSorting(sortOrder, fieldName, column);
    return column;
  }

  function createFilterableSortableNameColumn(title, fieldName) {
    return createFilterableSortableNameColumn(title, fieldName, fieldName);
  }

  function createFilterableCustomSortableNameColumn(
    title,
    fieldName,
    sortFieldName
  ) {
    let column = {
      title,
      render: (data) => (
        <NameCol>
          {data[dashboardFieldNames.secureProject] && (
            <Tooltip
              title={intl.formatMessage({ id: "project.isSecureMessage" })}
              trigger={["hover"]}
            >
              <FontAwesomeIcon icon={faLock} className="mr-2" />
            </Tooltip>
          )}
          {data[fieldName]}
        </NameCol>
      ),
      ...GetSearchFilterProps({
        setQueryParamFunction: (key, value) => {
          setQuery(key, value, queryParams, setQueryParams);
        },
        queryParamKey: _.camelCase(fieldName),
        queryParamValue: queryParams[_.camelCase(fieldName)],
      }),
    };

    column = configureColumnSorting(sortOrder, sortFieldName, column);
    return column;
  }

  function createFilterableBooleanDropdownColumn(title, fieldName) {
    return {
      title,
      render: (data) =>
        data[fieldName]
          ? intl.formatMessage({ id: "general.yes" })
          : intl.formatMessage({ id: "general.no" }),
      ...GetDropDownFilterProps({
        setQueryParamFunction: (key, value) => {
          setQuery(key, value, queryParams, setQueryParams);
        },
        queryParamKey: _.camelCase(fieldName),
        queryParamValue: queryParams[_.camelCase(fieldName)],
        singleChoice: true,
      }),
    };
  }

  function createFilterableSortableSmallerNameColumn(title, fieldName) {
    let column = {
      title,
      render: (data) => (
        <SmallerNameCol className="line-clamp-2">
          {data[fieldName]}
        </SmallerNameCol>
      ),
      ...GetSearchFilterProps({
        setQueryParamFunction: (key, value) => {
          setQuery(key, value, queryParams, setQueryParams);
        },
        queryParamKey: _.camelCase(fieldName),
        queryParamValue: queryParams[_.camelCase(fieldName)],
      }),
    };

    column = configureColumnSorting(sortOrder, fieldName, column);
    return column;
  }

  function createFilterableSortableSmallerUserNameColumn(
    title,
    fieldName,
    sortOnFieldName,
    getUserDisplayFunction
  ) {
    let column = {
      title,
      render: (data) => (
        <SmallerNameCol>
          {getUserDisplayFunction(data[fieldName])}
        </SmallerNameCol>
      ),
      ...GetSearchFilterProps({
        setQueryParamFunction: (key, value) => {
          setQuery(key, value, queryParams, setQueryParams);
        },
        queryParamKey: _.camelCase(sortOnFieldName),
        queryParamValue: queryParams[_.camelCase(sortOnFieldName)],
      }),
    };

    column = configureColumnSorting(sortOrder, sortOnFieldName, column);
    return column;
  }

  function createSortableDateColumn(title, fieldName) {
    let column = {
      title,
      render: (data) => (
        <SmallerNameCol>
          {dayjs(data[fieldName]).format(DATE_FORMATS.fourDigitYear)}
        </SmallerNameCol>
      ),
    };

    column = configureColumnSorting(sortOrder, fieldName, column);
    return column;
  }

  function createSortableSmallerNameColumn(title, fieldName) {
    let column = {
      title,
      render: (data) => <SmallerNameCol>{data[fieldName]}</SmallerNameCol>,
    };

    column = configureColumnSorting(sortOrder, fieldName, column);
    return column;
  }

  return {
    createCustomSortableSmallerNumColumn,
    createFilterableSortableDropdownColumn,
    createFilterableSortableNumberColumn,
    createFilterableSortableNameColumn,
    createFilterableCustomSortableNameColumn,
    createFilterableBooleanDropdownColumn,
    createFilterableSortableSmallerNameColumn,
    createSortableDateColumn,
    createSortableSmallerNameColumn,
    createIntakeViewLinkColumn,
    createMilestoneViewLinkColumn,
    createFilterableSortableSmallerUserNameColumn,
    handleChange,
  };
}
